// Which bulk actions the selection bar offers
// (`extension-alert-candidate-profile-actions-provider.service.ng1.ts:231`).
//
// Six possible actions, each behind its own combination of whitelabel flags, the alert's
// origin and whether a handler exists at all — and the bar then shows the first TWO and hides
// the rest behind an ellipsis. Which two you get is therefore decided by this list's contents
// and order, so it is built once, here, and tested.

/**
 * ⚠️ THE VALUES ARE KEBAB-CASE AND THEY REACH THE DOM. The template writes
 * `ng-class="'slim-view-' + action.name + '-all'"`, so `AddToJob` must be `add-to-job` —
 * `slim-view-add-to-job-all` is a real selector in the stylesheet and `slim-view-addToJob-all`
 * matches nothing. A camelCase transcription of the enum keys type-checks, renders, and
 * silently drops the hover colours off half the bulk bar.
 *
 * The four that never appear in the BULK bar are here anyway, because the numbers below are
 * keyed by the whole enum and two of them share an order with an action that does.
 */
export const ProfileAction = {
  Download: 'download',
  Favor: 'favor',
  Archive: 'archive',
  Message: 'message',
  Export: 'export',
  AddToJob: 'add-to-job',
  AssignToJob: 'assign-to-job',
  ExportToCsv: 'export-to-csv',
  SendCandidateDetails: 'send-candidate-details',
  ReportEnrichment: 'report-enrichment',
  AddToAutomation: 'add-to-automation',
} as const
export type ProfileAction = (typeof ProfileAction)[keyof typeof ProfileAction]

export interface BulkAction {
  name: ProfileAction
  order: number
  icon: string
  title: string
  /**
   * ⚠️ NOT "IS IT IN THE LIST". Favor and Archive are pushed with `display: false` and stay in
   * the array; the template filters `| filter: { display: true }` in three separate places.
   * Dropping them at construction time would be simpler and would change the `length > 2`
   * test that decides whether the ellipsis appears.
   *
   * ⚠️ AND THAT `false` IS A PLACEHOLDER, NOT THE ANSWER. `setCurrCandidateShownOption`
   * (extension-alert-candidates.component.ng1.ts:2269) overwrites both the moment the shown
   * filter is applied — which is on init — with `shownOption.name !== 'Favorites'` and
   * `!== 'Archived'`. So on the default "All" view they are BOTH TRUE, and taking the
   * constructor's `false` at face value loses two entries from the overflow menu and, because
   * the ellipsis is gated on `length > 2`, can remove the menu altogether.
   */
  display: boolean
}

/**
 * `ProfileActionOrder` — the bar sorts by this, not by construction order, and the first two
 * of the sorted list are the only ones drawn as icons.
 *
 * ⚠️ IT IS NOT THE CONSTRUCTION ORDER, AND FAVOR/ARCHIVE ARE LAST, NOT FIRST. They are
 * pushed first in `getBulkActions` and sort to 6 — so reading the order off the code that
 * builds the list puts Favor and Archive at the front and Message behind Export, which is
 * exactly one position wrong in the only place it shows: the second inline icon becomes the
 * upload arrow instead of the envelope. Two actions also SHARE a number (AddToJob/AssignToJob
 * at 2, ExportToCsv/SendCandidateDetails at 5) because each pair is mutually exclusive.
 */
export const PROFILE_ACTION_ORDER: Record<ProfileAction, number> = {
  [ProfileAction.Download]: 0,
  [ProfileAction.AddToAutomation]: 1,
  [ProfileAction.AddToJob]: 2,
  [ProfileAction.AssignToJob]: 2,
  [ProfileAction.Message]: 3,
  [ProfileAction.Export]: 4,
  [ProfileAction.ExportToCsv]: 5,
  [ProfileAction.SendCandidateDetails]: 5,
  [ProfileAction.Favor]: 6,
  [ProfileAction.Archive]: 6,
  [ProfileAction.ReportEnrichment]: 7,
}

/**
 * Each of these is a FEATURE FLAG **AND** a view-model boolean, not one or the other
 * (`extension-alert-candidate-profile-actions-provider.service.ng1.ts:328-348`):
 *
 *     addToAtsJobEnabled   = featureFlags.ADD_TO_JOB          && addToJobEnabled
 *     exportCandidates     = featureFlags.EXPORT_CANDIDATES   && exportCandidatesEnabled
 *                          ‖ featureFlags.EXPORT_CANDIDATES   && exportCandidatesToExistingListEnabled
 *     messageCandidates    = featureFlags.SHORTLIST_MESSAGE_CANDIDATES
 *     exportToFile         = NOT featureFlags.EXPORT_TO_FILE_DISABLED   ← negated, and DEFAULT ON
 *
 * ⚠️ THE LAST ONE IS INVERTED AND DEFAULTS ON. A tenant with no flags at all still offers
 * "Export to file"; reading `EXPORT_TO_FILE_*` as an enabling flag hides it everywhere, and
 * because the bar shows only the first two actions the effect is usually invisible — it just
 * quietly drops one entry out of the overflow menu.
 */
export interface BulkActionFlags {
  addToAtsJobEnabled?: boolean
  exportCandidatesEnabled?: boolean
  messageCandidatesEnabled?: boolean
  exportToFileEnabled?: boolean
  /** Only offered when the list came from an ATS job. */
  fromExtensionJob?: boolean
  /**
   * The current "Shown" filter (`IShowFilterOption.name`). Only Favor and Archive read it, and
   * only to hide themselves on their own view.
   */
  shownOptionName?: string
  /**
   * `getAtsLabel($window)` — named in "Add to <ATS> automation" (5cfd3ef899). The add-to-job and
   * export titles vary by ATS IDENTITY instead, which is why they arrive resolved.
   */
  atsLabel?: string
  addToJobTitle?: string
  exportTitle?: string
}

export interface BulkActionAlert {
  hostSite?: string | null
}

/** `IShowFilterOption.name` for the two views that hide their own action. */
const SHOWN_FAVORITES = 'Favorites'
const SHOWN_ARCHIVED = 'Archived'

/**
 * ⚠️ `hunterNotFromHostSearch` IS NOT "HAS NO HOST SITE". Upstream writes
 * `!entityAlert.hostSite || entityAlert.hostSite !== HostSite.Linkedin` — so an Indeed or Reed
 * import still gets Favor, Archive and Export, and only a LINKEDIN one loses them. Reading it
 * as "not an imported search" removes those three from four of the five import sources.
 */
function notFromHostSearch(alert: BulkActionAlert | undefined): boolean {
  if (alert === undefined) return false
  const host = alert.hostSite
  return host === undefined || host === null || host === '' || host !== 'Linkedin'
}

export function bulkActions(alert: BulkActionAlert | undefined, flags: BulkActionFlags = {}): BulkAction[] {
  const actions: BulkAction[] = []
  const notHostSearch = notFromHostSearch(alert)

  if (notHostSearch) {
    // `display` is the SHOWN FILTER's answer, not the constructor's — see the field's note.
    actions.push(
      {
        name: ProfileAction.Favor,
        order: PROFILE_ACTION_ORDER[ProfileAction.Favor],
        icon: 'fas fa-heart',
        title: 'Favorite',
        display: flags.shownOptionName !== SHOWN_FAVORITES,
      },
      {
        name: ProfileAction.Archive,
        order: PROFILE_ACTION_ORDER[ProfileAction.Archive],
        icon: 'fas fa-user-slash',
        title: 'Archive',
        display: flags.shownOptionName !== SHOWN_ARCHIVED,
      },
    )
  }

  if (flags.addToAtsJobEnabled === true) {
    actions.push({
      name: ProfileAction.AddToJob,
      order: PROFILE_ACTION_ORDER[ProfileAction.AddToJob],
      icon: 'fas fa-layer-plus',
      title: flags.addToJobTitle ?? 'Add to job',
      display: true,
    })
  }

  if (flags.exportCandidatesEnabled === true && notHostSearch) {
    actions.push({
      name: ProfileAction.Export,
      order: PROFILE_ACTION_ORDER[ProfileAction.Export],
      icon: 'fas fa-upload',
      title: flags.exportTitle ?? 'Export',
      display: true,
    })
  }

  if (flags.messageCandidatesEnabled === true) {
    actions.push({
      name: ProfileAction.Message,
      order: PROFILE_ACTION_ORDER[ProfileAction.Message],
      icon: 'fas fa-envelope',
      title: 'Send a message',
      display: true,
    })
  }

  if (flags.exportToFileEnabled === true) {
    actions.push({
      name: ProfileAction.ExportToCsv,
      order: PROFILE_ACTION_ORDER[ProfileAction.ExportToCsv],
      icon: 'fas fa-file-csv',
      title: 'Export to file',
      display: true,
    })
  }

  if (flags.fromExtensionJob === true) {
    actions.push({
      name: ProfileAction.AddToAutomation,
      order: PROFILE_ACTION_ORDER[ProfileAction.AddToAutomation],
      icon: 'fas fa-wand-magic',
      title: `Add to ${flags.atsLabel ?? 'ATS'} automation`,
      display: true,
    })
  }

  return actions
}

/** The two shown inline: displayable, ordered, first two. */
export function inlineBulkActions(actions: readonly BulkAction[]): BulkAction[] {
  return actions
    .filter((action) => action.display)
    .sort((a, b) => a.order - b.order)
    .slice(0, 2)
}

/**
 * The rest, behind the ellipsis.
 *
 * ⚠️ THE ELLIPSIS APPEARS ON `displayable.length > 2`, which is the same list this slices — so
 * the two have to be derived from one filter. Upstream writes the filter three times and the
 * `limitTo` bounds differ between them (`2 : 0` inline, `length - 2 : 2` in the menu), which is
 * the kind of pair that drifts.
 */
export function overflowBulkActions(actions: readonly BulkAction[]): BulkAction[] {
  return actions
    .filter((action) => action.display)
    .sort((a, b) => a.order - b.order)
    .slice(2)
}
